import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage, useIntl } from 'react-intl';
import Cookies from 'universal-cookie';
import { ThemeMode } from 'config';
import useConfig from 'hooks/useConfig';
import AccountContextListItem from 'components/accountContext/AccountContextListItem';
import Transitions from 'ui-component/extended/Transitions';
import TruncateTooltip from 'commonComponent/TruncateTooltip';
import { useOrganization } from 'contexts/OrganizationContext';
import {
  extractLiveAccountContexts,
  filterAccountContexts,
  getAccountContextTitle,
  getActiveContext,
  hasMultipleAccountContexts,
  loadPersistedAccountContexts,
  persistEnrichedLiveContexts,
  syncActiveManagerAccessFromLive,
} from 'utils/accountContext/accountContextUtils';
import {
  buildSwitchLoginPayload,
  continueWithAccountContext,
} from 'utils/accountContext/performAccountSwitch';
import useApi from 'hooks/useApi';

const cookies = new Cookies();

export default function AccountContextSection() {
  const theme = useTheme();
  const isDark = theme.palette.mode === ThemeMode.DARK;
  const location = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const { showLoader, hideLoader } = useConfig();
  const { updateOrganizationContext, refreshHierarchyTree } = useOrganization();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [contexts, setContexts] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [switchingId, setSwitchingId] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [searchText, setSearchText] = useState('');

  const myInsightTitle = intl.formatMessage({ id: 'myInsightAccount', defaultMessage: 'My Insight Account' });
  const filteredContexts = useMemo(
    () => filterAccountContexts(contexts, searchText, { myInsightTitle }),
    [contexts, searchText, myInsightTitle]
  );

  const refreshActiveLabel = useCallback(() => {
    if (!hasMultipleAccountContexts()) {
      setLabel('');
      setContexts([]);
      setActiveId('');
      return;
    }
    const persisted = loadPersistedAccountContexts();
    setContexts(persisted);
    const active = getActiveContext(persisted, { activeAccountId: cookies.get('accountId') });
    if (!active) {
      setLabel('');
      setActiveId('');
      return;
    }
    setActiveId(active.id);
    setLabel(getAccountContextTitle(active, intl));
  }, [intl]);

  useEffect(() => {
    refreshActiveLabel();
  }, [refreshActiveLabel, location.pathname, location.state?.accountSwitched]);

  const handleToggle = async () => {
    if (switchingId) return;
    const nextOpen = !open;
    setOpen(nextOpen);
    if (!nextOpen) {
      setSearchText('');
      return;
    }

    setSearchText('');
    setLoadingList(true);
    try {
      const result = await useApi('GET_ACCOUNT_CONTEXTS');
      const { ok, contexts: liveContexts } = extractLiveAccountContexts(result);
      if (ok) {
        const live = persistEnrichedLiveContexts(liveContexts);
        setContexts(live);
        // Mid-session demotion/promotion: stamp cookies without a full switch.
        syncActiveManagerAccessFromLive(live, cookies);
        const active = getActiveContext(live, { activeAccountId: cookies.get('accountId') });
        if (active) {
          setActiveId(active.id);
          setLabel(getAccountContextTitle(active, intl));
        } else {
          // Active account revoked or missing from live list — do not keep a stale
          // label/activeId (would show the wrong account and leave the menu open).
          setActiveId('');
          setLabel('');
          setOpen(false);
          // Remaining context(s): picker recovers a sole leftover account.
          // Empty live list: picker redirects to auth (hiding the switcher alone
          // would leave stale cookies with no recovery path).
          navigate('/account-selection', {
            replace: true,
            state: { contexts: live, switchMode: true, accessRevoked: true },
          });
        }
      } else {
        refreshActiveLabel();
      }
    } catch {
      refreshActiveLabel();
    } finally {
      setLoadingList(false);
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
    setSearchText('');
  };

  const handleSelect = async (context) => {
    if (!context || context.id === activeId || switchingId) {
      setOpen(false);
      setSearchText('');
      return;
    }

    setSwitchingId(context.id);
    setOpen(false);
    setSearchText('');
    showLoader(
      intl.formatMessage({ id: 'switchingAccount', defaultMessage: 'Switching your account…' })
    );

    try {
      const result = await continueWithAccountContext({
        selected: context,
        loginPayload: buildSwitchLoginPayload(),
        switchMode: true,
        navigate,
        updateOrganizationContext,
        refreshHierarchyTree,
        allOrganizationsLabel: intl.formatMessage({
          id: 'allOrganizations',
          defaultMessage: 'All Organizations',
        }),
        onManagerAccessRevoked: (live) => {
          setContexts(live);
          navigate('/account-selection', {
            replace: true,
            state: { contexts: live, switchMode: true, accessRevoked: true },
          });
        },
      });

      if (result.ok) {
        refreshActiveLabel();
      }
    } finally {
      setSwitchingId('');
      hideLoader();
    }
  };

  if (!label) {
    return null;
  }

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        alignSelf: 'center',
        flexShrink: 0,
        ml: 1,
      }}
    >
      <Box
        ref={anchorRef}
        id="account-context-switcher-trigger"
        data-testid="account-context-switcher"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={open}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleToggle();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          minWidth: 0,
          maxWidth: 200,
          px: 1.5,
          py: 0.75,
          borderRadius: '8px',
          cursor: 'pointer',
          bgcolor: isDark ? 'dark.800' : 'transparent',
          color: isDark ? 'text.primary' : 'common.white',
          border: isDark ? `1px solid ${theme.palette.divider}` : 'none',
          '&:hover': {
            bgcolor: isDark ? 'dark.900' : alpha(theme.palette.common.white, 0.08),
          },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
          <TruncateTooltip
            sx={{
              maxWidth: '100%',
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.25,
              color: 'inherit',
              display: 'block',
            }}
          >
            {label}
          </TruncateTooltip>
          <Typography
            noWrap
            sx={{
              fontSize: 11,
              fontWeight: 400,
              lineHeight: 1.25,
              color: isDark ? 'text.secondary' : alpha(theme.palette.common.white, 0.72),
            }}
          >
            <FormattedMessage id="accountNameCaption" defaultMessage="Account Name" />
          </Typography>
        </Box>
        {switchingId ? (
          <CircularProgress size={16} sx={{ color: 'inherit', flexShrink: 0 }} />
        ) : (
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 20,
              flexShrink: 0,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s ease',
            }}
          />
        )}
      </Box>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: { offset: [0, 8] },
          },
        ]}
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper
              elevation={0}
              sx={{
                width: 340,
                maxWidth: 'calc(100vw - 32px)',
                borderRadius: '12px',
                overflow: 'hidden',
                border: `1px solid ${isDark ? theme.palette.divider : '#E0E0E0'}`,
                boxShadow: isDark
                  ? '0 8px 24px rgba(0,0,0,0.4)'
                  : '0 8px 24px rgba(33, 33, 33, 0.12)',
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box sx={{ p: 2 }}>
                  <Typography
                    sx={{
                      px: 0.25,
                      pb: 1.25,
                      color: isDark ? 'text.primary' : '#212121',
                      fontWeight: 600,
                      fontSize: 14,
                      lineHeight: 1.3,
                    }}
                  >
                    <FormattedMessage id="switchAccountsTitle" defaultMessage="Switch Accounts" />
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                    placeholder={intl.formatMessage({
                      id: 'accountSelectionSearchPlaceholder',
                      defaultMessage: 'Search accounts',
                    })}
                    inputProps={{
                      'aria-label': intl.formatMessage({
                        id: 'accountSelectionSearchPlaceholder',
                        defaultMessage: 'Search accounts',
                      }),
                      'data-testid': 'account-switcher-search',
                    }}
                    sx={{
                      mb: 1.5,
                      '& .MuiOutlinedInput-root': {
                        height: 36,
                        borderRadius: '8px',
                        bgcolor: isDark ? 'dark.800' : 'common.white',
                        '& fieldset': {
                          borderColor: isDark ? undefined : '#E0E0E0',
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontSize: 13,
                      },
                      '& .MuiInputAdornment-root': {
                        color: isDark ? 'text.secondary' : '#9E9E9E',
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchText ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label={intl.formatMessage({ id: 'clear', defaultMessage: 'Clear' })}
                            onClick={() => setSearchText('')}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                  {loadingList ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : filteredContexts.length === 0 ? (
                    <Typography
                      sx={{
                        color: isDark ? 'text.secondary' : '#757575',
                        textAlign: 'center',
                        py: 2,
                        fontSize: 13,
                      }}
                    >
                      <FormattedMessage
                        id="accountSelectionNoMatches"
                        defaultMessage="No accounts match your search."
                      />
                    </Typography>
                  ) : (
                    <Box
                      data-testid="account-switcher-list"
                      sx={{
                        maxHeight: 320,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        pr: 0.25,
                        // Keep scrollbar usable when many accounts
                        WebkitOverflowScrolling: 'touch',
                      }}
                    >
                      <Stack spacing={1.25}>
                        {filteredContexts.map((context) => (
                          <AccountContextListItem
                            key={context.id}
                            context={context}
                            intl={intl}
                            dense
                            showCheck
                            selected={context.id === activeId}
                            disabled={Boolean(switchingId)}
                            onSelect={() => handleSelect(context)}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
